import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { jsonStorage, uid } from './persist';
import type { Profile, ResponsibleLimits } from '../lib/api';
import { randomSeed } from '../lib/hash';

export type Tier = Profile['tier'];

const TIER_THRESHOLDS: Array<{ tier: Tier; wagered: number }> = [
  { tier: 'diamond', wagered: 5_000_000 },
  { tier: 'platinum', wagered: 1_000_000 },
  { tier: 'gold', wagered: 250_000 },
  { tier: 'silver', wagered: 50_000 },
  { tier: 'bronze', wagered: 0 },
];

export function tierForWagered(wagered: number): Tier {
  return TIER_THRESHOLDS.find((t) => wagered >= t.wagered)?.tier ?? 'bronze';
}

export function tierProgress(wagered: number): { tier: Tier; next: Tier | null; progress: number } {
  const index = TIER_THRESHOLDS.findIndex((t) => wagered >= t.wagered);
  const current = TIER_THRESHOLDS[index]!;
  const next = index > 0 ? TIER_THRESHOLDS[index - 1]! : null;
  if (!next) return { tier: current.tier, next: null, progress: 1 };
  const span = next.wagered - current.wagered;
  const progress = Math.min(Math.max((wagered - current.wagered) / span, 0), 1);
  return { tier: current.tier, next: next.tier, progress };
}

const DEFAULT_LIMITS: ResponsibleLimits = {
  dailyWagerLimit: null,
  weeklyWagerLimit: null,
  sessionMinutes: null,
  lossLimit: null,
  coolOffUntil: null,
  selfExcluded: false,
};

export interface AuthState {
  profile: Profile | null;
  isGuest: boolean;
  sessionStartedAt: string | null;
  register: (username: string, email: string, country?: string) => void;
  loginAsGuest: (username?: string) => void;
  logout: () => void;
  updateProfile: (patch: Partial<Profile>) => void;
  updateLimits: (limits: Partial<ResponsibleLimits>) => void;
  beginSession: () => void;
}

function makeProfile(username: string, email: string, country = 'US'): Profile {
  return {
    id: `usr_${randomSeed(6)}`,
    username,
    email,
    avatarSeed: randomSeed(4),
    tier: 'bronze',
    createdAt: new Date().toISOString(),
    kycStatus: 'unverified',
    country,
    limits: { ...DEFAULT_LIMITS },
  };
}

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      profile: null,
      isGuest: true,
      sessionStartedAt: null,

      register: (username, email, country) =>
        set({ profile: makeProfile(username, email, country), isGuest: false }),

      loginAsGuest: (username = 'Guest') => {
        const existing = get().profile;
        set({
          profile: existing ?? makeProfile(username, `${username.toLowerCase()}@guest.agave`),
          isGuest: true,
        });
      },

      logout: () => set({ profile: null, isGuest: true, sessionStartedAt: null }),

      updateProfile: (patch) => {
        const profile = get().profile;
        if (!profile) return;
        set({ profile: { ...profile, ...patch } });
      },

      updateLimits: (limits) => {
        const profile = get().profile;
        if (!profile) return;
        set({ profile: { ...profile, limits: { ...profile.limits, ...limits } } });
      },

      beginSession: () => set({ sessionStartedAt: new Date().toISOString() }),
    }),
    { name: 'auth', storage: jsonStorage },
  ),
);

/** Ensure a profile exists so the app is usable immediately. */
export function ensureProfile(): Profile {
  const { profile, loginAsGuest } = useAuth.getState();
  if (profile) return profile;
  const randNum = (parseInt(randomSeed(2), 16) % 9000) + 1000;
  const guest = `Guest${randNum}`;
  loginAsGuest(guest);
  return useAuth.getState().profile!;
}

export { DEFAULT_LIMITS, uid };
